/* Contacts — the table and the drawer beside it.

   Variant A of mockups/crm-v3-contacts-v1.html (section #a), approved
   2026-09-06: a dense table you scan 40 rows at a time, and a drawer that
   opens next to it rather than over it, so the row you clicked keeps its
   place. Ported from the legacy People screen (2026-08-26), rebuilt here for
   CRM v3 slice 3.

   Two rules this file exists to keep:
   - Every column is derived in listModel.js, over data already in memory. The
     list never reads the `interactions` collection; Last touch comes from the
     stored `lastTouch` field. The drawer, one record at a time, still uses
     useInteractions.
   - Money is contactValueIndex → src/apps/jobs/lib/revenue.js. Nothing here
     adds up grossRevenue by hand.

   Not in this slice: the "Due for a touch" strip and the drawer's "Why now"
   box (slice 8). Save as segment arrived with slice 4 — the filter row hands
   its state to listModel's contactFiltersToRules and the Segments tab opens on
   an unsaved audience. */
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { C, inp, sel, btnS, btnP, btnD } from "../../lib/styles.js";
import { fmt } from "../../lib/utils.js";
import {
  INDUSTRY_LABELS, LEAD_SOURCES, CONTACT_ROLES, ROLE_COLORS,
} from "../../lib/constants.js";
import { setConsent } from "../../lib/consent.js";
import ActivityTimeline from "./ActivityTimeline.jsx";
import { useInteractions } from "./useInteractions.js";
import { S, layout, cellStyle, ROW_CLASS, consentDotStyle } from "./listStyles.js";
import { Chips, SelectPill, TableFooter, ListRowStyle } from "./ListParts.jsx";
import {
  buildContactRows, filterContactRows, sortContactRows, paginate,
  ROLE_CHIPS, CONSENT_FILTERS, INDUSTRY_FILTERS, TOUCH_WINDOWS, CONTACT_SORTS,
  VALUE_BUCKET_LABELS, parseTags, commitIfStillActive, contactFiltersToRules,
  canSaveContactsAsSegment, contactsSaveBlockedReason,
} from "./listModel.js";

const ACTIVITY_CAP = 6;

/** Today in New York, as YYYY-MM-DD — what setConsent() requires for `at`. */
const todayNY = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

const arr = (v) => (Array.isArray(v) ? v : v ? [v] : []);

export default function ContactsView({
  contacts,
  clients,
  jobs,
  savedQuotes,
  saveContact,
  removeContact,
  openClientFrom,
  activeContact,
  setActiveContact,
  navigateBack,
  returnTo,
  isMobile,
  paletteOpen,
  onSaveAsSegment,
}) {
  const [roleFilter, setRoleFilter] = useState("all");
  const [consentFilter, setConsentFilter] = useState("any");
  const [industryFilter, setIndustryFilter] = useState("any");
  const [touchFilter, setTouchFilter] = useState("any");
  const [sortKey, setSortKey] = useState("touch");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);

  /* Escape closes the drawer and puts focus back on the row it came from, so
     the keyboard never loses its place in a 40-row table. */
  const rowRefs = useRef({});

  const safeContacts = useMemo(() => contacts || [], [contacts]);
  const safeClients = useMemo(() => clients || [], [clients]);
  const safeJobs = useMemo(() => jobs || [], [jobs]);
  const safeQuotes = useMemo(() => savedQuotes || [], [savedQuotes]);

  /* One pass over the whole book: the value index walks the jobs once for
     every contact, so nothing below costs more than an array scan. */
  const rows = useMemo(
    () => buildContactRows({ contacts: safeContacts, clients: safeClients, jobs: safeJobs, quotes: safeQuotes }),
    [safeContacts, safeClients, safeJobs, safeQuotes],
  );

  const visible = useMemo(() => sortContactRows(
    filterContactRows(rows, {
      role: roleFilter, consent: consentFilter, industry: industryFilter, touch: touchFilter,
    }),
    sortKey,
  ), [rows, roleFilter, consentFilter, industryFilter, touchFilter, sortKey]);

  const filterKey = `${roleFilter}|${consentFilter}|${industryFilter}|${touchFilter}|${sortKey}`;
  const [lastFilterKey, setLastFilterKey] = useState(filterKey);
  if (filterKey !== lastFilterKey) { setLastFilterKey(filterKey); setPage(1); }

  const pageState = paginate(visible, page);

  const open = activeContact ? (safeContacts.find(c => c.id === activeContact.id) || activeContact) : null;
  const openRow = open ? rows.find(r => r.id === open.id) : null;
  const openId = open?.id ?? null;

  /* Every edit state belongs to ONE record, and it is reset from the id rather
     than from the click handlers. A contact can become the open one by a row
     click, by ⌘K, by a linked-contact row inside a client drawer or by the
     back link, and only two of those went through a handler that cleared the
     draft — so half-typed edits to Ben used to appear, still half-typed, over
     Dave's details. */
  const [lastOpenId, setLastOpenId] = useState(openId);
  if (openId !== lastOpenId) {
    setLastOpenId(openId);
    setEditing(false);
    setDraft(null);
    setShowAllActivity(false);
  }

  /* A save is a network round trip, and the operator can move on while it is
     in flight. The ref is what "still on screen" means at the moment the write
     lands, which a closure over `open` could not say. */
  const activeIdRef = useRef(openId);
  useEffect(() => { activeIdRef.current = openId; }, [openId]);
  const commitSaved = useCallback(
    (record) => commitIfStillActive(activeIdRef.current, record, setActiveContact),
    [setActiveContact],
  );

  const closeDrawer = useCallback(() => {
    if (navigateBack) navigateBack(); else setActiveContact(null);
    // The row may be off this page (opened from search); focusing nothing is
    // the correct answer then, not an exception.
    if (openId) requestAnimationFrame(() => rowRefs.current[openId]?.focus?.());
  }, [openId, navigateBack, setActiveContact]);

  useEffect(() => {
    /* While the ⌘K palette is up it owns Escape. Both handlers sit on the
       window, so without this guard one Escape closed the palette AND the
       drawer behind it — the operator loses the record they were reading for
       the sake of dismissing a search they opened over it. */
    if (!openId || paletteOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId, paletteOpen, closeDrawer]);

  // Hooks must run on every render, so this sits above the drawer branch and
  // is a no-op when nothing is open.
  const activity = useInteractions({ contactIds: open ? [open.id] : [] });

  const drawerOpen = Boolean(open);

  const table = (
    <div>
      <ListRowStyle />
      <div style={S.box}>
        <div style={S.scroller}>
          <table style={S.table} data-testid="contacts-table">
            <thead>
              <tr>
                <th style={S.th}>Contact</th>
                <th style={S.th}>Role</th>
                <th style={S.th}>Consent</th>
                <th style={{ ...S.th, ...S.thRight }}>Value</th>
                <th style={S.th}>Last touch</th>
                <th style={S.th}>LinkedIn</th>
                {/* With the drawer open the table is 380px narrower, so the
                    column that carries the least goes rather than pushing
                    Value and Last touch out of sight. */}
                {!drawerOpen && <th style={S.th}>Tags</th>}
              </tr>
            </thead>
            <tbody>
              {pageState.rows.map((r) => {
                const selected = open?.id === r.id;
                const cell = (base, first) => cellStyle(base, selected, first);
                return (
                  <tr
                    key={r.id}
                    // Dropped when the row unmounts, so paging through a large
                    // book does not keep every detached <tr> ever rendered.
                    ref={(el) => { if (el) rowRefs.current[r.id] = el; else delete rowRefs.current[r.id]; }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    className={ROW_CLASS}
                    data-testid={`contact-row-${r.id}`}
                    style={{ cursor: "pointer", height: 44 }}
                    onClick={() => setActiveContact(r.record)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActiveContact(r.record);
                      }
                    }}
                  >
                    <td style={cell(null, true)}>
                      <div style={S.who}>
                        <div style={S.whoName}>{r.name || "Unnamed"}</div>
                        <div style={S.whoMeta}>
                          {r.company}
                          {r.company && r.email && <span style={S.dot}>·</span>}
                          {r.email}
                        </div>
                      </div>
                    </td>
                    <td style={cell()}>
                      {r.roles.map((role) => (
                        <span key={role} style={{
                          ...S.role,
                          background: `${ROLE_COLORS[role] || C.muted}12`,
                          color: ROLE_COLORS[role] || C.muted,
                        }}>
                          {CONTACT_ROLES[role] || role}
                        </span>
                      ))}
                    </td>
                    <td style={cell()}>
                      <span style={S.consent}>
                        <i style={consentDotStyle(r.consent.tone)} />
                        {r.consent.label}
                        {r.consent.nda && <span style={{ ...S.small, marginLeft: 4 }}>NDA</span>}
                      </span>
                    </td>
                    <td style={cell({ ...S.mono, ...S.tdRight })}>
                      {r.value.amount === null ? "—" : fmt(r.value.amount)}
                      {r.value.note && (
                        <span style={S.small}>
                          {r.value.quoted ? `quoted ${fmt(r.value.quoted)}` : r.value.note}
                        </span>
                      )}
                    </td>
                    <td style={cell(S.touch)}>
                      {r.touchRel
                        ? <><span style={S.touchNow}>{r.touchRel}</span>{r.touchRel === "today" ? "" : " ago"}</>
                        : "—"}
                      {r.touch.label && <span style={S.touchChannel}>{r.touch.label}</span>}
                    </td>
                    <td style={cell()}>
                      {r.linkedin.connected
                        ? <span style={S.linkedin}>connected</span>
                        : r.linkedin.url
                          ? <span style={S.linkedinOff}>not connected</span>
                          : <span style={S.linkedinOff}>—</span>}
                    </td>
                    {!drawerOpen && (
                      <td style={cell()}>
                        {r.tags.map((t) => <span key={t} style={S.tag}>{t}</span>)}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pageState.total === 0 && (
          <div style={S.empty}>
            {safeContacts.length === 0 ? "No contacts yet." : "No contacts match these filters."}
          </div>
        )}
        <TableFooter
          page={pageState}
          noun="contact"
          sortLabel={CONTACT_SORTS.find(s => s.key === sortKey)?.label}
          onPage={setPage}
        />
      </div>
    </div>
  );

  return (
    <div>
      <div style={S.filters} data-testid="contacts-filters">
        <Chips options={ROLE_CHIPS} value={roleFilter} onChange={setRoleFilter} testid="contacts-role" />
        <span style={S.gap} />
        <SelectPill label="Consent" value={consentFilter} options={CONSENT_FILTERS}
          onChange={setConsentFilter} testid="contacts-consent-filter" />
        <SelectPill label="Industry" value={industryFilter} options={INDUSTRY_FILTERS}
          onChange={setIndustryFilter} testid="contacts-industry-filter" />
        <SelectPill label="Last touch" value={touchFilter} options={TOUCH_WINDOWS}
          onChange={setTouchFilter} testid="contacts-touch-filter" />
        <SelectPill label="Sort" value={sortKey} options={CONTACT_SORTS}
          onChange={setSortKey} testid="contacts-sort" />
        {onSaveAsSegment && (() => {
          /* A list filtered to No bulk or Unsubscribed has no audience in it:
             a segment's consent row can only narrow within the bulk-eligible
             pair. Saving one would drop the filter and hand back a WIDER
             audience than the screen. See canSaveContactsAsSegment. */
          const filters = { consent: consentFilter };
          const can = canSaveContactsAsSegment(filters);
          return (
            <button
              type="button"
              style={{
                ...btnS, padding: "5px 11px", fontSize: 11,
                ...(can ? null : { opacity: 0.45, cursor: "not-allowed" }),
              }}
              data-testid="contacts-save-as-segment"
              disabled={!can}
              title={can ? "Turn these filters into a saved audience" : contactsSaveBlockedReason(filters)}
              onClick={() => onSaveAsSegment(contactFiltersToRules({
                role: roleFilter, consent: consentFilter, industry: industryFilter, touch: touchFilter,
              }))}
            >
              Save as segment
            </button>
          );
        })()}
        <span style={S.count} data-testid="contacts-count">
          {visible.length.toLocaleString()} contact{visible.length === 1 ? "" : "s"}
        </span>
      </div>

      <div style={layout(drawerOpen, isMobile)} data-testid="contacts-layout">
        {/* On a phone the drawer takes the screen, as the CRM already does. */}
        {(!drawerOpen || !isMobile) && table}
        {drawerOpen && (
          <ContactDrawer
            row={openRow}
            contact={open}
            clients={safeClients}
            activity={activity}
            showAllActivity={showAllActivity}
            setShowAllActivity={setShowAllActivity}
            editing={editing}
            setEditing={setEditing}
            draft={draft}
            setDraft={setDraft}
            saveContact={saveContact}
            removeContact={removeContact}
            commitSaved={commitSaved}
            openClientFrom={openClientFrom}
            onClose={closeDrawer}
            returnTo={returnTo}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
}

/* ═══ the drawer ═══════════════════════════════════════════════════════════ */

function ContactDrawer({
  row, contact: ct, clients, activity, showAllActivity, setShowAllActivity,
  editing, setEditing, draft, setDraft, saveContact, removeContact,
  commitSaved, openClientFrom, onClose, returnTo, isMobile,
}) {
  const d = draft || ct;
  const set = (field, val) => setDraft(prev => ({ ...(prev || ct), [field]: val }));
  const eInp = { ...inp, fontSize: 12, padding: "5px 8px" };
  const eSel = { ...sel, fontSize: 12, padding: "5px 8px" };

  const emails = arr(ct.emails);
  const roles = row?.roles || (arr(ct.role).length ? arr(ct.role) : ["prospect"]);
  const consent = row?.consent;
  const client = row?.client || clients.find(c => c.id === ct.clientId) || null;
  const linkedin = ct.social?.linkedin || {};
  const linkedinUrl = typeof linkedin === "string" ? linkedin : linkedin.url || "";
  const buckets = row?.buckets || { direct: {}, viaCompany: {}, referred: {} };
  const title = ct.jobTitle || ct.title || "";

  const records = activity.records || [];
  const shown = showAllActivity ? records : records.slice(0, ACTIVITY_CAP);

  const markUnsubscribed = async () => {
    if (!window.confirm(`Mark ${ct.name || "this contact"} unsubscribed? Only a website or Mailchimp opt-in by them reverses it.`)) return;
    try {
      const next = setConsent(ct, {
        state: "no_bulk",
        source: "manual",
        at: todayNY(),
        unsubscribed: true,
        note: "Marked unsubscribed in the Suite",
      });
      const toSave = { ...ct, consent: next };
      await saveContact(toSave);
      commitSaved(toSave);
    } catch (err) {
      window.alert("Could not record the unsubscribe: " + err.message);
    }
  };

  const save = async () => {
    const toSave = { ...d };
    if (toSave._leadSourceOther) toSave.leadSource = toSave._leadSourceOther;
    delete toSave._leadSourceOther;
    toSave.emails = arr(toSave.emails).filter(e => String(e).trim());
    // Tags are held as raw text while the field is being typed in and split
    // only here. See parseTags in listModel.js.
    toSave.tags = parseTags(toSave._tagsText ?? arr(toSave.tags).join(", "));
    delete toSave._tagsText;
    if (!arr(toSave.role).length) toSave.role = ["prospect"];
    // Consent is never written from this form — it has its own action, and
    // src/lib/consent.js owns every consent write.
    toSave.consent = ct.consent;
    toSave.social = { ...(ct.social || {}), ...(toSave.social || {}) };
    try {
      await saveContact(toSave);
      // Only the record that was being saved closes its editor: if the user has
      // moved on and started editing another record, that draft is theirs.
      // commitSaved() says whether the saved record is still the open one.
      if (commitSaved(toSave)) { setEditing(false); setDraft(null); }
    } catch (err) {
      window.alert("Save failed: " + err.message);
    }
  };

  const leadSourceKey = Object.keys(LEAD_SOURCES).includes(d.leadSource)
    ? d.leadSource : d.leadSource ? "_freeform" : "";

  return (
    <div style={isMobile ? S.drawerMobile : S.drawer} data-testid="contact-drawer">
      <div style={S.dhead}>
        <div style={S.dback}>
          <button type="button" style={S.dbackBtn} onClick={onClose} data-testid="contact-drawer-back">
            ← {returnTo === "clients" ? "Clients" : "Contacts"}
          </button>
          <span style={{ opacity: 0.8 }}>esc</span>
        </div>
        <div style={S.dname}>{ct.name || "Unnamed"}</div>
        <div style={S.dco}>
          {client
            ? <button type="button" style={S.dcoLink} onClick={() => openClientFrom(client, "contacts")}>{client.name}</button>
            : (ct.company || "")}
          {title && <span> · {title}</span>}
        </div>
        <div style={S.dchips}>
          {roles.map(r => (
            <span key={r} style={{
              ...S.role, marginRight: 0,
              background: `${ROLE_COLORS[r] || C.muted}12`, color: ROLE_COLORS[r] || C.muted,
            }}>
              {CONTACT_ROLES[r] || r}
            </span>
          ))}
          {consent && (
            <span style={{ ...S.consent, marginLeft: 4 }}>
              <i style={consentDotStyle(consent.tone)} />
              {consent.label}{consent.nda ? " · NDA" : ""}
            </span>
          )}
        </div>
      </div>

      {/* ── Details ── */}
      <div style={S.dsec}>
        <div style={S.dsecTitle}>
          <span>Details</span>
          <button
            type="button"
            style={S.dsecAction}
            data-testid="contact-drawer-edit"
            onClick={() => {
              if (editing) { setEditing(false); setDraft(null); }
              else { setEditing(true); setDraft({ ...ct, _tagsText: arr(ct.tags).join(", ") }); }
            }}
          >
            {editing ? "Cancel" : "Edit"}
          </button>
        </div>

        {editing ? (
          <div style={{ display: "grid", gap: 8, fontSize: 12 }}>
            <div>
              <div style={S.kvK}>Emails</div>
              {arr(d.emails).map((email, i) => (
                <div key={i} style={{ display: "flex", gap: 4, alignItems: "center", marginBottom: 4 }}>
                  <input
                    style={{ ...eInp, flex: 1 }}
                    value={email}
                    aria-label={i === 0 ? "Primary email" : `Email ${i + 1}`}
                    onChange={ev => { const u = [...arr(d.emails)]; u[i] = ev.target.value; set("emails", u); }}
                  />
                  {arr(d.emails).length > 1 && (
                    <button style={{ ...btnS, padding: "2px 6px", fontSize: 9 }}
                      onClick={() => set("emails", arr(d.emails).filter((_, x) => x !== i))}>×</button>
                  )}
                </div>
              ))}
              <button style={{ ...btnS, padding: "3px 8px", fontSize: 10 }}
                onClick={() => set("emails", [...arr(d.emails), ""])}>+ Add email</button>
            </div>
            <label>
              <div style={S.kvK}>Phone</div>
              <input style={eInp} value={d.phone || ""} onChange={ev => set("phone", ev.target.value)} />
            </label>
            <label>
              <div style={S.kvK}>LinkedIn</div>
              <input
                style={eInp}
                placeholder="https://www.linkedin.com/in/…"
                value={(typeof d.social?.linkedin === "string" ? d.social.linkedin : d.social?.linkedin?.url) || ""}
                onChange={ev => set("social", {
                  ...(d.social || {}),
                  linkedin: { ...(typeof d.social?.linkedin === "object" ? d.social.linkedin : {}), url: ev.target.value },
                })}
              />
            </label>
            <div>
              <div style={S.kvK}>Industry</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {Object.entries(INDUSTRY_LABELS).map(([k, v]) => {
                  const checked = arr(d.industry).includes(k);
                  return (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer" }}>
                      <input type="checkbox" checked={checked} style={{ accentColor: C.teal }}
                        onChange={() => set("industry", checked
                          ? arr(d.industry).filter(x => x !== k)
                          : [...arr(d.industry), k])} />
                      {v}
                    </label>
                  );
                })}
              </div>
            </div>
            <div>
              <div style={S.kvK}>Roles</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {Object.entries(CONTACT_ROLES).map(([k, v]) => {
                  const checked = arr(d.role).includes(k);
                  return (
                    <label key={k} style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, cursor: "pointer" }}>
                      <input type="checkbox" checked={checked} style={{ accentColor: ROLE_COLORS[k] || C.muted }}
                        onChange={() => set("role", checked
                          ? arr(d.role).filter(x => x !== k)
                          : [...arr(d.role), k])} />
                      {v}
                    </label>
                  );
                })}
              </div>
            </div>
            <label>
              <div style={S.kvK}>Source</div>
              <select style={eSel} value={leadSourceKey} onChange={ev => {
                const val = ev.target.value;
                if (val === "_freeform") {
                  set("_leadSourceOther", Object.keys(LEAD_SOURCES).includes(d.leadSource) ? "" : (d.leadSource || ""));
                  set("leadSource", "other");
                } else { set("leadSource", val); set("_leadSourceOther", undefined); }
              }}>
                <option value="">—</option>
                {Object.entries(LEAD_SOURCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                <option value="_freeform">Other (specify)</option>
              </select>
              {leadSourceKey === "_freeform" && (
                <input style={{ ...eInp, marginTop: 4 }} value={d._leadSourceOther ?? ""}
                  onChange={ev => set("_leadSourceOther", ev.target.value)} placeholder="Specify…" />
              )}
            </label>
            <label>
              <div style={S.kvK}>Tags</div>
              {/* Raw text until Save. Splitting on every keystroke made the
                  field impossible to type in: "alpha, beta" lost the space
                  and then the comma the moment either was typed. */}
              <input
                style={eInp}
                data-testid="contact-drawer-tags"
                value={d._tagsText ?? arr(d.tags).join(", ")}
                placeholder="comma separated"
                onChange={ev => set("_tagsText", ev.target.value)}
              />
            </label>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button style={{ ...btnP, padding: "5px 14px", fontSize: 11 }} onClick={save} data-testid="contact-drawer-save">Save</button>
              <button style={{ ...btnS, padding: "5px 14px", fontSize: 11 }}
                onClick={() => { setEditing(false); setDraft(null); }}>Cancel</button>
              <button
                style={{ ...btnD, padding: "5px 12px", fontSize: 11, marginLeft: "auto" }}
                onClick={() => {
                  if (!window.confirm(`Permanently delete "${ct.name}"? This cannot be undone.`)) return;
                  removeContact(ct.id);
                  onClose();
                }}
              >Delete</button>
            </div>
          </div>
        ) : (
          <>
            <div style={S.kv}>
              <span style={S.kvK}>Email</span>
              <span style={S.kvV}>{emails.length ? emails.join(", ") : "—"}</span>
              <span style={S.kvK}>Phone</span>
              <span style={{ ...S.kvV, ...S.kvVMono }}>{ct.phone || "—"}</span>
              <span style={S.kvK}>LinkedIn</span>
              <span style={S.kvV}>
                {linkedin.connected
                  ? `connected${linkedin.connectedOn ? ` · ${linkedin.connectedOn}` : ""}`
                  : linkedinUrl || "—"}
              </span>
              <span style={S.kvK}>Industry</span>
              <span style={S.kvV}>
                {arr(ct.industry).map(k => INDUSTRY_LABELS[k] || k).join(", ") || "—"}
              </span>
              <span style={S.kvK}>Source</span>
              <span style={S.kvV}>{LEAD_SOURCES[ct.leadSource] || ct.leadSource || "—"}</span>
              <span style={S.kvK}>Tags</span>
              <span style={S.kvV} data-testid="contact-drawer-tag-list">
                {arr(ct.tags).length
                  ? arr(ct.tags).map(t => <span key={t} style={S.tag}>{t}</span>)
                  : "—"}
              </span>
            </div>
            {!ct.consent?.unsubscribed && (
              <button
                type="button"
                style={{ ...S.danger, marginTop: 10 }}
                data-testid="contact-drawer-unsubscribe"
                onClick={markUnsubscribed}
              >
                Mark unsubscribed
              </button>
            )}
          </>
        )}
      </div>

      {/* ── Associated value ── */}
      <div style={S.dsec}>
        <div style={S.dsecTitle}><span>Associated value</span></div>
        <div style={S.tiles} data-testid="contact-drawer-value">
          {[
            { k: "direct", b: buckets.direct },
            { k: "viaCompany", b: buckets.viaCompany },
            { k: "referred", b: buckets.referred },
          ].map(({ k, b }) => (
            <div key={k} style={S.tile}>
              <div style={S.tileN}>{fmt(b?.amount || 0)}</div>
              <div style={S.tileL}>
                {b?.count ? `${b.count} ${VALUE_BUCKET_LABELS[k]}` : VALUE_BUCKET_LABELS[k]}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Activity ── */}
      <div style={S.dsecLast} data-testid="contact-drawer-activity">
        <ActivityTimeline
          variant="contact"
          records={shown}
          loading={activity.loading}
          error={activity.error}
        />
        {records.length > ACTIVITY_CAP && (
          <button
            type="button"
            style={S.moreBtn}
            data-testid="contact-drawer-activity-toggle"
            onClick={() => setShowAllActivity(v => !v)}
          >
            {showAllActivity ? `Newest ${ACTIVITY_CAP}` : `All ${records.length}`}
          </button>
        )}
      </div>
    </div>
  );
}
